/**
 * Creates a partial object containing the base typography styles for each
 * element rendered by `Text` that should have default typography styles.
 *
 * @param theme - The current theme object.
 * @returns The partial object with base typography styles.
 */
const getBaseTypographyStyles = (theme, as) => {
  const {
    typography
  } = theme;
  const baseTypographyStyles = {
    p: typography['text-body'],
    small: typography['text-body-small'],
    code: typography['text-code'],
    h1: typography['display-heading1'],
    h2: typography['display-heading2'],
    h3: typography['display-heading3'],
    h4: typography['display-heading4'],
    h5: typography['display-heading5'],
    h6: typography['display-heading6']
  };
  return baseTypographyStyles[as];
};

/**
 * Creates a styles overrides object based on the user-provided style props.
 *
 * @param theme - The current theme object.
 * @param styleProps - The original style props provided to the component.
 * @returns The styles overrides object.
 */
const getUserProvidedTypographyStyles = (theme, styleProps) => {
  const {
    typography,
    fontFamily,
    fontSize,
    fontWeight,
    lineHeight
  } = styleProps;
  if (typography) {
    return theme.typography[typography];
  }
  const individualStyles = {};
  if (fontFamily) individualStyles.fontFamily = theme.fontFamily[fontFamily];
  if (fontSize) individualStyles.fontSize = theme.fontSize[fontSize];
  if (fontWeight) individualStyles.fontWeight = theme.fontWeight[fontWeight];
  if (lineHeight) individualStyles.lineHeight = theme.lineHeight[lineHeight];
  return individualStyles;
};

/**
 * Customizes the styles for the `Text` component based on the `as` prop.
 *
 * @param as - The DOM element to render for `Text`.
 * @param opts - An options object provided by the `useMapStylePropsToCSS`
 * hook to further customize the final styles object.
 * @returns The customized CSS styles object that will be passed to the
 * styled component rendered by `Text`.
 */
export const customizeStyles = (as, opts) => {
  const {
    theme,
    styleProps,
    cssStyles
  } = opts;
  switch (as) {
    // Elements without default typography styles
    case 'span':
    case 'strong':
    case 'legend':
    case 'em':
      return cssStyles;

    // Elements with default typography styles
    case 'p':
    case 'small':
    case 'code':
    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
    case 'h5':
    case 'h6':
      {
        const baseTypographyStyles = getBaseTypographyStyles(theme, as);
        const userProvidedStyles = getUserProvidedTypographyStyles(theme, styleProps);
        return Object.assign({}, cssStyles, baseTypographyStyles, userProvidedStyles);
      }
    default:
      {
        const __exhaustiveCheck = as;
        throw new Error(`Unhandled \`as\` argument:\n ${as}`);
      }
  }
};
export const generateTextTruncateStyles = truncate => {
  /**
   * Note that we should move to non-vendor prefixed properties once line-clamp
   * is standardized [MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/-webkit-line-clamp).
   * We should also consider updating the use of '-webkit-box-orient': 'vertical', to use
   * logical properties ('block-axis' instead of 'vertical' and 'inline-axis' instead of
   * 'horizontal') at that time.
   */

  return {
    '-webkit-box-orient': 'vertical',
    '-webkit-line-clamp': `${typeof truncate === 'object' ? truncate.lineClamp : 1}`,
    display: '-webkit-box',
    overflow: 'hidden'
  };
};